import { useWhatsNew } from '@src/stores-v2/StoreContext';
import { formatDate } from '@src/utility/dateFormat';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import ReleaseCard from './ReleaseCard';

const ChangelogHistory: React.FC = observer(() => {
  const whatsNew = useWhatsNew();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (whatsNew.allReleases.length === 0) {
      whatsNew.fetchAll();
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    whatsNew.fetchAll();
  }, [whatsNew]);

  const toggleExpanded = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  return (
    <Container>
      {whatsNew.allReleases.map(release => (
        <AccordionItem key={release.id} $expanded={expandedId === release.id}>
          <AccordionHeader onClick={() => toggleExpanded(release.id)}>
            <HeaderContent>
              <ReleaseName>{release.name}</ReleaseName>
              <ReleaseDate>
                {formatDate(release.publishedAt) ?? release.publishedAt}
              </ReleaseDate>
            </HeaderContent>
            <Chevron $expanded={expandedId === release.id}>&#8250;</Chevron>
          </AccordionHeader>
          {expandedId === release.id && (
            <AccordionBody>
              <ReleaseCard release={release} hideDate hideTitle />
            </AccordionBody>
          )}
        </AccordionItem>
      ))}

      {whatsNew.isLoading && <StatusText>Loading...</StatusText>}

      {whatsNew.canLoadMore && !whatsNew.isLoading && (
        <LoadMoreButton onClick={handleLoadMore}>
          Load more releases
        </LoadMoreButton>
      )}
    </Container>
  );
});

export default ChangelogHistory;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Breathing room above the first card (only in the list view). */
  padding-top: var(--spacing-lg);
`;

const AccordionItem = styled.div<{ $expanded: boolean }>`
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius);
  background: var(--surface-subtle);
  overflow: hidden;

  ${p =>
    p.$expanded &&
    `
    background: var(--surface-elevated);
  `}
`;

const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--foreground);
  border-radius: var(--radius-input);
  transition: background var(--transition-normal) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--surface-elevated);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
  min-width: 0;
`;

const ReleaseName = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReleaseDate = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const Chevron = styled.span<{ $expanded: boolean }>`
  font-size: 18px;
  color: var(--text-muted);
  transition: transform var(--transition-fast) cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${p => (p.$expanded ? '90deg' : '0deg')});
  flex-shrink: 0;
  margin-left: var(--spacing-sm);
`;

const AccordionBody = styled.div`
  padding: 0 var(--spacing-md) var(--spacing-sm);
`;

const StatusText = styled.div`
  text-align: center;
  padding: var(--spacing-lg);
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

const LoadMoreButton = styled.button`
  display: block;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  margin-top: var(--spacing-sm);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
    color: var(--foreground);
  }
`;
